'use strict';

import { DepthFormat, DepthTexture, FloatType, NearestFilter, Vector2, WebGLRenderTarget } from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader';
import { DOF } from './Passes/DOF';
import { ScreenSpaceAmbientOcclusion } from './Passes/SSAO';
import { Bloom } from './Passes/Bloom';
import { ToneMapping } from './Passes/ToneMapping';
import { TransitionPass } from './Passes/TransitionPass';
import { isDev, isMobile, threeJsVariables } from '../../settings';
import { CylinderAnimation } from './Passes/CylinderAnimation';
import { FogEffect } from './Passes/Fog';
import { GTAOLite } from './Passes/GTAOLite';
import { SceneLUT } from './Passes/SceneLUT';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass';
import { MaskObject } from './Passes/MaskObjectPass';
import { MixerEffect } from './Passes/MixerPass';
import { DynamicExposureEffect } from './Passes/DynamicExposurePass';
import { HighlightsCompressionEffect } from './Passes/HighlightsCompression';
import { ShadowBoostEffect } from './Passes/ShadowBoost';

export class Composer {
  #samples = config.threejs.effectComposer?.samples || 0;
  #effects = config.threejs.effectComposer?.effects || [
    {
      "name": "ssao",
      "active": false,
      "params": {
        "kernelRadius": 8,
        "minDistance": 0.005,
        "maxDistance": 0.1
      }
    },
    {
      "name": "gtao",
      "active": false,
      "params": {
        "aoRadius": 0.25,
        "distanceExponent": 1,
        "thickness": 1,
        "scale": 1,
        "aoSamples": 16,
        "distanceFallOff": 1,
        "screenSpaceRadius": true,
        "lumaPhi": 10,
        "depthPhi": 2,
        "normalPhi": 3,
        "pdRadius": 4,
        "radiusExponent": 1,
        "rings": 2,
        "pdSamples": 16
      }
    },
    {
      "name": "fog",
      "active": false,
      "params": {
        "fogColor": '#dedede',
        "fogNear": 0.7,
        "fogFar": 0.35,
        "fogDensity": 1
      }
    },
    {
      "name": "dof",
      "active": false,
      "params": {
        "aperture": 0.025,
        "maxblur": 0.01
      }
    },
    {
      "name": "bloom",
      "active": false,
      "params": {
        "strength": 1.5,
        "radius": 0.4,
        "threshold": 0.85
      }
    },
    {
      "name": "toneMapping",
      "active": false,
      "params": {
        "middleGrey": 0.6,
        "maxLuminance": 16,
        "averageLuminance": 1
      }
    }
  ];

  constructor (renderer,scene,camera) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;
    this.updatables = [];
    this.passes = {};
    let needFXAA = false;
    
    this.registerNewEffects();

    const depthTexture = new DepthTexture();
    depthTexture.magFilter = NearestFilter;
    depthTexture.minFilter = NearestFilter;
    depthTexture.format = DepthFormat;
    depthTexture.type = FloatType;
    const size = this.renderer.getSize( new Vector2() );

    if (this.renderer.capabilities.isWebGL2 && !isMobile) {
      let samples = Number(this.#samples) === 0 ? Math.trunc(this.renderer.capabilities.maxSamples / 2) : Number(this.#samples);
      samples = threeJsVariables.quality.composerSamples(samples);
      if (samples > this.renderer.capabilities.maxSamples) {
        samples = this.renderer.capabilities.maxSamples;
      }
      const renderTarget = new WebGLRenderTarget( size.width, size.height, { samples: samples, depthTexture } );
      this.composer = new EffectComposer(this.renderer,renderTarget);
    } else {
      const renderTarget = new WebGLRenderTarget( size.width, size.height, { depthTexture } );
      this.composer = new EffectComposer(this.renderer,renderTarget);
      needFXAA = true;
    }

    this.setPixelRatio(threeJsVariables.quality.pixelRatio());

    const scenePass = new RenderPass(this.scene,this.camera);
    scenePass.name = 'ScenePass';
    this.composer.addPass(scenePass);

    this.fxaaPass = new ShaderPass(FXAAShader);
    this.fxaaPass.name = 'FXAA';
    const pixelRatio = this.renderer.getPixelRatio();
    this.fxaaPass.material.uniforms[ 'resolution' ].value.x = 1 / ( this.renderer.domElement.offsetWidth * pixelRatio );
    this.fxaaPass.material.uniforms[ 'resolution' ].value.y = 1 / ( this.renderer.domElement.offsetHeight * pixelRatio );
    this.fxaaPass.enabled = needFXAA;
    this.composer.addPass(this.fxaaPass);
    this.copyPass = new OutputPass();
    this.copyPass.name = 'CopyPass';
    this.composer.addPass(this.copyPass);

    this.position = 1;

    this.addEffects();
    this.#debug();
  }

  addEffects() {
    this.#effects.forEach(effect => {
      let scope = null;
      switch (effect.name) {
        case 'lut':
          scope = new SceneLUT();
          scope.config = effect.params;
          break;
        case 'toneMapping':
          scope = new ToneMapping(effect.params);
          break;
        case 'bloom':
          scope = new Bloom(this.renderer,effect.params);
          break;
        // case 'sao':
        //   scope = new ScalableAmbientOcclusion(this.renderer,this.scene,this.camera,effect.params);
        //   break;
        case 'ssao':
          scope = new ScreenSpaceAmbientOcclusion(this.camera,effect.params);
          break;
        case 'gtao':
          scope = new GTAOLite(this.camera);
          scope.config = effect.params;
          break;
        case 'dof':
          scope = new DOF(this.camera,effect.params);
          break;
        case 'fog':
          scope = new FogEffect(this.camera,window.innerWidth,window.innerHeight,effect.params);
          scope.defaultParams.active = effect.active;
          break;
        case 'dynamicExposure':
          scope = new DynamicExposureEffect(threeJsVariables.renderer,window.innerWidth,window.innerHeight);
          scope.config = effect.params;
          this.addUpdatableElement(scope);
          break;
        case 'highlightsCompression':
          scope = new HighlightsCompressionEffect();
          scope.config = effect.params;
          break;
        case 'shadowBoost':
          scope = new ShadowBoostEffect();
          scope.config = effect.params;
          break;
        default:
          console.warn(`Unregistered effect - ${effect.name}`);
          break;
      }

      if (scope) {
        scope.active = effect.active;
        this.addPass(effect.name,scope);
      }

    });

    // Registering other effects
    const mapTransition = new TransitionPass();
    mapTransition.active = false;
    this.addPass('map',mapTransition);
    
    const size = this.renderer.getSize( new Vector2() );
    const cylinderTransition = new CylinderAnimation(this.camera,size.x,size.y);
    this.addPass('cylinderPass',cylinderTransition);
    
    const maskObject = new MaskObject(this.camera,size.x,size.y);
    this.addPass('maskObjectPass',maskObject);
    
    const mixerPass = new MixerEffect(this.camera,size.x,size.y);
    this.addPass('mixerPass',mixerPass);
    // this.composer.insertPass(cylinderTransition.effect,2);
    
  }
  
  registerNewEffects() {
    if (!this.#effects.find(effect => effect.name === 'gtao')) {
      this.#effects.splice(1,0,{
        "name": "gtao",
        "active": false,
        "params": {
          "aoRadius": 0.25,
          "distanceExponent": 1,
          "thickness": 1,
          "scale": 1,
          "aoSamples": 16,
          "distanceFallOff": 1,
          "screenSpaceRadius": true,
          "lumaPhi": 10,
          "depthPhi": 2,
          "normalPhi": 3,
          "pdRadius": 4,
          "radiusExponent": 1,
          "rings": 2,
          "pdSamples": 16
        }
      });
    }
    
    if (!this.#effects.find(effect => effect.name === 'fog')) {
      this.#effects.splice(2,0,{
        "name": "fog",
        "active": false,
        "params": {
          "fogColor": '#dedede',
          "fogNear": 0.7,
          "fogFar": 0.35,
          "fogDensity": 1
        }
      })
    }
  
    if (!this.#effects.find(effect => effect.name === 'dynamicExposure')) {
      this.#effects.push({
        "name": "dynamicExposure",
        "active": false,
        "params" : {
          "targetLuminance": 0.5,
          "exposureSpeed": 2,
          "minExposure": 1,
          "maxExposure": 1.35
        }
      });
    }
    
    if (!this.#effects.find(effect => effect.name === 'highlightsCompression')) {
      this.#effects.push({
        "name": "highlightsCompression",
        "active": false,
        "params": {
          "highlightThreshold": 0.911106,
          "highlightCompression": 5
        }
      })
    }
    
    if (!this.#effects.find(effect => effect.name === 'shadowBoost')) {
      this.#effects.push({
        "name": "shadowBoost",
        "active": false,
        "params" : {
          "shadowThreshold": 0.2,
          "shadowBoost": 5
        }
      })
    }
    
  
    if (!this.#effects.find(effect => effect.name === 'lut')) {
      this.#effects.push({
        "name": 'lut',
        "active": false,
        "params": {
          "intensity": 1
        }
      });
    }
  }

  addPass(name,effectScope) {
    this.composer.insertPass(effectScope.effect,this.position);
    this.passes[name] = effectScope;
    this.position++;
  }
  
  addUpdatableElement(element) {
    this.updatables.push(element);
  }
  
  removeUpdatableElement(element) {
    this.updatables = this.updatables.filter(el => el !== element);
  }

  passToggle(name,state) {
    if (this.passes[name]) {
      this.passes[name].enabled = state;
    }
  }

  getPass(name) {
    return this.passes[name];
  }

  setPixelRatio(ratio) {
    this.composer.setPixelRatio(ratio);
  }
  
  getPixelRatio() {
    return this.composer._pixelRatio;
  }

  setSize(x,y) {
    this.composer.setSize(x,y);
  }

  render(delta) {
    for (let i = 0; i < this.updatables.length; i++) {
      this.updatables[i].tick(delta);
    }
    this.composer.render(delta);
  }

  #debug() {
    if (!isDev || !this.#effects.length) return;

    this.debugParams = {};
    const functions = {
      export: () => this.#download()
    };

    const mainFolder = threeJsVariables.gui.addFolder('Effect composer');
    this.#effects.forEach(effect => {
      const effectFolder = mainFolder.addFolder(effect.name);
      this.debugParams[effect.name] = {};
      this.debugParams[effect.name] = JSON.parse(JSON.stringify(effect.params));
      this.debugParams[effect.name].active = effect.active;

      Object.entries(this.debugParams[effect.name]).forEach(([key,value]) => {
        if (typeof value === 'boolean') {
          effectFolder.add(this.debugParams[effect.name],key).onChange((v) => this.#debugListener(effect.name,key,v));
        } else {
          effectFolder.add(this.debugParams[effect.name],key).onChange((v) => this.#debugListener(effect.name,key,v));
        }
      });
    });

    mainFolder.add(functions,'export');
  }

  #debugListener(effectName,key,value) {
    const pass = this.getPass(effectName);
    if (!pass) return;

    if (key === 'active') {
      pass.active = value;
    } else {
      switch (effectName) {
        case 'toneMapping':

          switch (key) {
            case 'middleGrey':
              pass.effect.setMiddleGrey(value);
              break;
            case 'maxLuminance':
              pass.effect.setMaxLuminance(value);
              break;
            case 'averageLuminance':
              pass.effect.setAverageLuminance(value);
              break;

          }

          break;
        case 'dof':
          pass.effect.uniforms[key].value = value;
          break;
        case 'sao':
          pass.effect.params[key] = value;
          break;
        case 'ssao':
          pass.effect.uniforms[key].value = value;
          break;
        case 'fog':
          switch (key) {
            case 'fogColor':
              pass.setColor(value);
              break;
            case 'fogNear':
              pass.setNear(value);
              break;
            case 'fogFar':
              pass.setFar(value);
              break;
            case 'fogDensity':
              pass.setDensity(value);
              break;
          }
          break;
        default:
          pass.effect[key] = value;
          break;

      }
    }
  }

  #download() {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(this.debugParams));
    const href = document.createElement('a');
    href.setAttribute('href',dataStr);
    href.setAttribute('download',`${config.projectTitle}_effects.json`);
    href.click();
  }

  disableAll() {
    Object.values(this.passes).forEach(pass => pass.active = false);
  }

}
